import * as React from 'react';
import { View } from 'react-native';

// Web shim for `@react-native-community/blur`.
//
// Implementation strategy:
// - Prefer CSS `backdrop-filter: blur(...)` (works on modern Chromium/Safari).
// - Fallback to a translucent overlay color when unsupported.

type BlurType =
  | 'xlight'
  | 'light'
  | 'dark'
  | 'regular'
  | 'prominent'
  | 'extraDark'
  | 'materialDark'
  | 'materialLight'
  | 'material'
  | string;

type BlurViewProps = React.ComponentProps<typeof View> & {
  blurAmount?: number;
  blurType?: BlurType;
  reducedTransparencyFallbackColor?: string;
};

function supportsBackdropFilter() {
  const CSSAny = (globalThis as any)?.CSS;
  if (!CSSAny?.supports) return false;
  return (
    CSSAny.supports('backdrop-filter', 'blur(1px)') ||
    CSSAny.supports('-webkit-backdrop-filter', 'blur(1px)')
  );
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function blurTypeOverlay(blurType?: BlurType) {
  switch (blurType) {
    case 'dark':
    case 'extraDark':
    case 'materialDark':
      return 'rgba(0,0,0,0.35)';
    case 'light':
    case 'xlight':
    case 'materialLight':
      return 'rgba(255,255,255,0.35)';
    case 'prominent':
      return 'rgba(255,255,255,0.22)';
    case 'regular':
    case 'material':
    default:
      return 'rgba(255,255,255,0.18)';
  }
}

function amountToPx(amount?: number) {
  // Native blurAmount is not px. Map values that look reasonable for typical usage:
  // e.g. blurAmount=100 (used in this repo) -> 20px blur.
  const a = typeof amount === 'number' ? amount : 10;
  const px = a / 5;
  return clamp(px, 0, 50);
}

export const BlurView = React.forwardRef<View, BlurViewProps>(
  (
    { blurAmount, blurType, reducedTransparencyFallbackColor, style, ...rest },
    ref,
  ) => {
    const hasBackdrop = React.useMemo(() => supportsBackdropFilter(), []);
    const overlayColor =
      reducedTransparencyFallbackColor ?? blurTypeOverlay(blurType);
    const blurPx = amountToPx(blurAmount);

    // RN-web style typing doesn't include backdropFilter, so cast to any.
    const webBlurStyle: any = hasBackdrop
      ? {
          backgroundColor: overlayColor,
          backdropFilter: `blur(${blurPx}px)`,
          WebkitBackdropFilter: `blur(${blurPx}px)`,
        }
      : { backgroundColor: overlayColor };

    return <View ref={ref} style={[webBlurStyle, style]} {...rest} />;
  },
);
BlurView.displayName = 'BlurView';

export const VibrancyView = React.forwardRef<View, BlurViewProps>(
  ({ style, ...rest }, ref) => {
    return <BlurView ref={ref} style={style} {...rest} />;
  },
);
VibrancyView.displayName = 'VibrancyView';

export default BlurView;
